import React from 'react';
import { connect } from 'react-redux';
import { cn } from 'lib/utils';
import FieldRow from './FieldRow';
import { getIssuesComponent, getErrorIssues, getWarningIssues } from '../../../../../../../utils/validationSummary';

/**
 * A property whose value is MINUTES SINCE MIDNIGHT, edited as a clock time.
 *
 * Unlike the other files in modern/ this has no legacy sibling to stay in sync with — it is
 * new, for MPPT's Sunrise Time and Sunset Time (dd range 0..1440). components/InputTime is
 * not reusable here: it works in "HH:MM:SS" strings and hands back a time string, because it
 * serves the Astro schedule tables where durations, not clock times, are the model.
 *
 * A native <input type="time"> rather than a custom widget, for the same reasons
 * modern/PropertyDropdown gives for its native <select>: correct keyboard and locale
 * behaviour for free, no bundle weight, and the OS picker on touch devices. It also renders
 * the separator in the viewer's own locale, which a text field spelling out "HH:mm" cannot.
 *
 * The outward contract matches every other property component: handleChange(propertyName,
 * value) with value in the property's own units, i.e. minutes as a string.
 *
 * Kept as a class, like its neighbours: componentDidUpdate compares incoming props against
 * the PREVIOUS STATE, which is what resyncs the display when a parent clamps or rejects a
 * value — props never change in that case, so a useEffect dependency array would not fire.
 */
class PropertyTimePicker extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            value: this.props.property.value,
        };

        this.handleChange = this.handleChange.bind(this);
    }

    /** minutes -> "HH:mm". 1440 is midnight at the far end of the day and has no distinct
     *  clock representation, so it shares 00:00 with 0. */
    toClock(minutes) {
        const m = Number(minutes);
        if (isNaN(m)) {
            return '';
        }
        const wrapped = ((m % 1440) + 1440) % 1440;
        const hh = Math.floor(wrapped / 60);
        const mm = wrapped % 60;
        return `${hh < 10 ? '0' : ''}${hh}:${mm < 10 ? '0' : ''}${mm}`;
    }

    /** "HH:mm" -> minutes. An empty or partial value clears back to 0 rather than NaN. */
    toMinutes(clock) {
        if (!clock) {
            return 0;
        }
        const [h, m] = clock.split(':');
        const hours = Number(h);
        const mins = Number(m);
        if (isNaN(hours) || isNaN(mins)) {
            return 0;
        }
        return hours * 60 + mins;
    }

    handleChange(e) {
        const value = '' + this.toMinutes(e.target.value);
        this.setState({ value: value });
        this.props.handleChange(this.props.property.info.name, value);
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.props.property.value !== prevState.value) {
            this.setState({ value: this.props.property.value });
        }
    }

    render() {
        const errors = getErrorIssues(this.props.issues, this.props.property);
        const warnings = getWarningIssues(this.props.issues, this.props.property);

        // `.danger` / `.warning` are standalone 2px border rules in stylesheet.css and still
        // apply on top of the Tailwind border below.
        const state = errors.length > 0 ? 'danger' : warnings.length > 0 ? 'warning' : '';

        return (
            <FieldRow label={this.props.title} unit={this.props.measureUnit} disabled={this.props.disabled} className={this.props.className}>
                <input
                    disabled={this.props.disabled}
                    className={cn(
                        'h-8 w-24 rounded-md border border-input bg-background px-2 text-center text-sm font-semibold',
                        'text-[var(--brand-color1)] shadow-sm outline-none',
                        'focus-visible:ring-1 focus-visible:ring-ring',
                        'disabled:cursor-not-allowed disabled:text-muted-foreground',
                        state
                    )}
                    name={this.props.property.info.name}
                    type="time"
                    step="60"
                    value={this.toClock(this.state.value ?? this.props.property.value)}
                    onChange={this.handleChange} />

                {getIssuesComponent(errors, warnings)}
            </FieldRow>
        );
    };
}

const mapStateToProps = (state, ownProps) => {
    return {
        validationSummary: state.project.validationSummary,
        issues: state.project.issueByProperty,
    };
};

const mapDispatchToProps = {
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(PropertyTimePicker);
